import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromParcelShop from './reducers';
import {
	ParcelShop,
	ParcelShopDistances,
	ParcelShopFilter,
	ParcelShopFeatures,
	ParcelShopFeature,
	FEATURE_OPEN,
	ParcelShopId,
	ParcelShopWithCalculatedDetails,
	LatLngBounds,
	LatLng,
} from '@app/models';
import { parcelShopIsOpen } from '@app/models/parcel-shop.utils';
import * as haversine from 'haversine-distance';

export const selectParcelShopState = createFeatureSelector<fromParcelShop.State>(fromParcelShop.featureKey);

export const selectClock = createSelector(selectParcelShopState, state => state.clock);

export const selectIsLoading = createSelector(selectParcelShopState, state => state.loading);

export const selectIsLoaded = createSelector(selectParcelShopState, state => state.loaded);

export const selectIsLoadedOrLoadring = createSelector(selectParcelShopState, state => state.loaded || state.loading);

export const selectParcelShops = createSelector(selectParcelShopState, state => state.parcelShops);

export const selectParcelShopsWithCalculatedDetails = createSelector(selectParcelShops, selectClock, (parcelShops, clock) => {
	// console.log(
	// 	'selectParcelShopsWithCalculatedDetails selector: recalculating details',
	// 	parcelShops.length,
	// 	clock
	// );
	return parcelShops.map(parcelShop => {
		const isOpenNow = parcelShopIsOpen(parcelShop, clock);
		return {
			...parcelShop,
			features: {
				...parcelShop.features,
				[FEATURE_OPEN]: isOpenNow.isOpen,
			},
			isOpenNow,
		} as ParcelShopWithCalculatedDetails;
	});
});

export const selectParcelShopsOrdered = createSelector(selectParcelShopsWithCalculatedDetails, parcelShops => [...parcelShops].sort((a, b) => a.name.localeCompare(b.name)));

export const selectParcelShop = (id: ParcelShopId) => createSelector(selectParcelShopsWithCalculatedDetails, parcelShops => parcelShops.find(parcelShop => parcelShop.id === id));

export const selectFilter = createSelector(selectParcelShopState, state => state.filter);

export const selectFilterProp = <P extends keyof ParcelShopFilter>(prop: P) => createSelector(selectParcelShopState, state => state.filter[prop]);

export const selectParcelShopDistances = createSelector(selectParcelShops, selectFilterProp('location'), (parcelShops, location: ParcelShopFilter['location']) => {
	if (!location) return null;
	// console.log('selectParcelShopDistances selector: recalculating distances', parcelShops.length, location);
	const distances = parcelShops.reduce((acc, parcelShop) => {
		acc[parcelShop.id] = haversine(location, parcelShop.location);
		return acc;
	}, {} as ParcelShopDistances);
	return distances;
});

export const selectParcelShopsFiltered = createSelector(selectParcelShopsWithCalculatedDetails, selectParcelShopDistances, selectFilter, (parcelShops, distances, { bounds, features }) => {
	return parcelShops
		.filter(bounds ? filterByBounds(bounds) : () => true)
		.filter(filterByFeatures(features))
		.map(parcelShop => ({
			...parcelShop,
			distance: distances?.[parcelShop.id],
		}))
		.sort((a, b) => (a.distance && b.distance ? a.distance - b.distance : 0));
});

export const selectParcelShopsFilteredByFeatures = createSelector(selectParcelShopsWithCalculatedDetails, selectFilterProp('features'), (parcelShops, features: ParcelShopFilter['features']) =>
	parcelShops.filter(filterByFeatures(features))
);

export const selectParcelLockerIds = createSelector(selectParcelShopsWithCalculatedDetails, parcelShops => {
	return parcelShops.filter(filterByFeatures({ 'gls-parcel-locker': true })).map(parcelShop => parcelShop.id);
});

const filterByBounds = (bounds: LatLngBounds) => (parcelShop: ParcelShop) => boundsContains(bounds, parcelShop.location);
const filterByFeatures = (features: Partial<ParcelShopFeatures>) => {
	const filteredFeatures = (Object.keys(features) as ParcelShopFeature[]).filter(k => features[k]);
	return (parcelShop: ParcelShop) => filteredFeatures.every(feature => parcelShop.features?.[feature]);
};

const boundsContains = (bounds: LatLngBounds, location: LatLng) => {
	const l = { lat: location[0], lng: location[1] };
	const ne = { lat: bounds[0][0], lng: bounds[0][1] };
	const sw = { lat: bounds[1][0], lng: bounds[1][1] };
	return l.lat >= sw.lat && l.lat <= ne.lat && l.lng >= sw.lng && l.lng <= ne.lng;
};
